// src/interaction/modes/DragItems.ts
// Fixed TypeScript errors by using proper tracking system
// ✨ ENHANCED: Icon replacement when dragging icon onto another icon

import { produce } from 'immer';
import { ModeActions, Coords } from '@/types';
import {
  getItemByIdOrThrow,
  setWindowCursor,
  getAnchorParent,
  getItemsAtTile
} from '@/utils';
import { groupManager } from '@/features/groups/GroupManager';
import { GroupableElement } from '@/features/groups/groupTypes';
import { normalizeConnectorAnchors } from '@/core/connections/waypointSimplification';
import { getAnchorTile } from '@/core/connections/connectionUtils';
import {
  getLayoutElement,
  LayoutElement,
  snapToObjects
} from '@/core/layout/layoutUtils';

// ✨ Store for managing icon replacement confirmation dialog (shared with PlaceIcon)
let iconReplacementPending: {
  sourceItemId: string;
  targetItemId: string;
  sourceIconId: string;
} | null = null;

// Callback to trigger confirmation dialog
let showDragReplaceDialog: ((params: {
  sourceItemId: string;
  targetItemId: string;
  sourceIconId: string;
}) => void) | null = null;

// ✨ Export function for registering dialog callback
export const registerDragReplaceDialog = (
  callback: (params: { sourceItemId: string; targetItemId: string; sourceIconId: string }) => void
) => {
  showDragReplaceDialog = callback;
};

// ✨ Export function for confirming replacement
export const confirmDragReplacement = (
  scene: any,
  uiStateActions: any
) => {
  if (!iconReplacementPending) return;

  const { sourceItemId, targetItemId, sourceIconId } = iconReplacementPending;

  // Update the target item's icon to the source icon
  scene.updateModelItem(targetItemId, { icon: sourceIconId });

  // The dragged source may belong to a group. Remove its membership before
  // deleting it so the group does not retain a dangling element reference.
  groupManager.removeFromGroup({ type: 'ITEM', id: sourceItemId });

  // Delete the source item (the one being dragged)
  scene.deleteModelItem(sourceItemId);
  scene.deleteViewItem(sourceItemId);

  // Select the updated target icon
  uiStateActions.setItemControls({
    type: 'ITEM',
    id: targetItemId
  });
  uiStateActions.setMode({
    type: 'CURSOR',
    showCursor: true,
    mousedownItem: null
  });
  uiStateActions.setDropTargetGroupId(null);
  uiStateActions.setSmartGuides([]);

  // Clear pending replacement
  iconReplacementPending = null;
};

// ✨ Export function for canceling replacement
export const cancelDragReplacement = (uiStateActions: any) => {
  // Clear the pending state
  iconReplacementPending = null;

  // Return to cursor mode
  uiStateActions.setMode({
    type: 'CURSOR',
    showCursor: true,
    mousedownItem: null
  });
};

// ✅ FIX: Use a Map to track original positions instead of modifying objects
const originalPositions = new Map<string, any>();
let lastProcessedDragTile: string | null = null;

// Helper to calculate group bounding box
const getGroupBoundingBox = (groupId: string, scene: any) => {
  const elements = groupManager.getGroupElements(groupId);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  elements.forEach(element => {
    let item: any = null;
    let fromX = 0;
    let fromY = 0;
    let toX = 0;
    let toY = 0;

    if (element.type === 'ITEM') {
      item = scene.items.find((i: any) => i.id === element.id);
      if (item) {
        fromX = item.tile.x;
        fromY = item.tile.y;
        toX = item.tile.x + 1;
        toY = item.tile.y + 1;
      }
    } else if (element.type === 'RECTANGLE') {
      item = scene.rectangles.find((r: any) => r.id === element.id);
      if (item) {
        fromX = Math.min(item.from.x, item.to.x);
        fromY = Math.min(item.from.y, item.to.y);
        toX = Math.max(item.from.x, item.to.x);
        toY = Math.max(item.from.y, item.to.y);
      }
    } else if (element.type === 'TEXTBOX') {
      item = scene.textBoxes.find((t: any) => t.id === element.id);
      if (item) {
        fromX = item.tile.x;
        fromY = item.tile.y;
        const width = item.size?.width || 2;
        toX = item.tile.x + width;
        toY = item.tile.y + 1;
      }
    } else if (element.type === 'CONNECTOR') {
      item = scene.connectors.find((c: any) => c.id === element.id);
      if (item && item.path && item.path.rectangle) {
        fromX = Math.min(item.path.rectangle.from.x, item.path.rectangle.to.x);
        fromY = Math.min(item.path.rectangle.from.y, item.path.rectangle.to.y);
        toX = Math.max(item.path.rectangle.from.x, item.path.rectangle.to.x);
        toY = Math.max(item.path.rectangle.from.y, item.path.rectangle.to.y);
      }
    }

    if (item) {
      minX = Math.min(minX, fromX);
      maxX = Math.max(maxX, toX);
      minY = Math.min(minY, fromY);
      maxY = Math.max(maxY, toY);
    }
  });

  if (minX === Infinity) return null;

  return {
    from: { x: minX, y: minY },
    to: { x: maxX, y: maxY }
  };
};

// Helper to check if a point is inside or near a bounding box
const isNearBoundingBox = (point: Coords, bbox: { from: Coords; to: Coords }, padding: number = 1) => {
  return (
    point.x >= bbox.from.x - padding &&
    point.x <= bbox.to.x + padding &&
    point.y >= bbox.from.y - padding &&
    point.y <= bbox.to.y + padding
  );
};

// Helper to find which group the mouse is hovering over
const findDropTargetGroup = (mousePos: Coords, scene: any, draggedItems: any[]): string | null => {
  const allGroups = groupManager.getAllGroups();

  for (const group of allGroups) {
    // ✅ Skip locked groups - cannot drop elements into locked groups
    if (group.locked) {
      continue;
    }

    const bbox = getGroupBoundingBox(group.id, scene);
    if (!bbox) continue;

    // Check if mouse is near this group's bounds
    if (isNearBoundingBox(mousePos, bbox, 1.5)) {
      // Check if any dragged item is NOT already in this group
      const canDrop = draggedItems.some(item => {
        const element: GroupableElement = { type: item.type as any, id: item.id };
        const itemGroup = groupManager.getGroupForElement(element);
        return !itemGroup || itemGroup.id !== group.id;
      });

      if (canDrop) {
        return group.id;
      }
    }
  }

  return null;
};

export const DragItems: ModeActions = {
  entry: ({ uiState, scene }) => {
    setWindowCursor('grabbing');
    lastProcessedDragTile = null;

    // ✅ Clear initial movement flag
    if (uiState.mode.type === 'DRAG_ITEMS') {
      const { items } = uiState.mode;

      // Commit the active waypoint from the mouseup coordinates. Mousemove is
      // not guaranteed to fire on the final snapped tile before release.
      items
        .filter((item) => item.type === 'CONNECTOR_ANCHOR')
        .forEach((item) => {
          const originalTile = originalPositions.get(`anchor-${item.id}`);
          if (!originalTile) return;
          try {
            const connector = getAnchorParent(item.id, scene.connectors);
            const targetTile = { ...uiState.mouse.position.tile };
            const movedAnchors = connector.anchors.map((anchor) =>
              anchor.id === item.id && anchor.ref.tile
                ? { ...anchor, ref: { tile: targetTile } }
                : anchor
            );
            const anchors = normalizeConnectorAnchors(
              movedAnchors,
              scene.currentView
            );
            scene.updateConnector(connector.id, { anchors });
            if (!anchors.some((anchor) => anchor.id === item.id)) {
              uiState.actions.setItemControls({
                type: 'CONNECTOR',
                id: connector.id
              });
            }
          } catch (error) {
            console.warn(`Could not commit connector waypoint ${item.id}:`, error);
          }
        });

      // ✅ Store original positions in our tracking Map
      items.forEach((item) => {
        try {
          if (item.type === 'ITEM') {
            const node = getItemByIdOrThrow(scene.items, item.id).value;
            originalPositions.set(`item-${item.id}`, { ...node.tile });
          } else if (item.type === 'RECTANGLE') {
            const rectangle = getItemByIdOrThrow(scene.rectangles, item.id).value;
            originalPositions.set(`rect-${item.id}`, {
              from: { ...rectangle.from },
              to: { ...rectangle.to }
            });
          } else if (item.type === 'TEXTBOX') {
            const textBox = getItemByIdOrThrow(scene.textBoxes, item.id).value;
            originalPositions.set(`textbox-${item.id}`, { ...textBox.tile });
          } else if (item.type === 'CONNECTOR_ANCHOR') {
            const connector = getAnchorParent(item.id, scene.connectors);
            const anchor = connector.anchors.find(a => a.id === item.id);
            if (anchor) {
              originalPositions.set(
                `anchor-${item.id}`,
                { ...getAnchorTile(anchor, scene.currentView) }
              );
            }
          }
        } catch (error) {
          console.warn(`Could not store original position for ${item.type} ${item.id}`);
        }
      });

      uiState.actions.setMode(
        produce(uiState.mode, (draft) => {
          draft.isInitialMovement = false;
        })
      );
    }
  },

  exit: ({ uiState }) => {
    setWindowCursor('default');
    lastProcessedDragTile = null;
    // ✅ Clear tracking Map on exit
    originalPositions.clear();
    // Clear drop target highlight
    uiState.actions.setDropTargetGroupId(null);
    uiState.actions.setSmartGuides([]);
  },

  mousemove: ({ uiState, scene }) => {
    if (uiState.mode.type !== 'DRAG_ITEMS') return;

    const { items } = uiState.mode;
    if (!items.length) return;

    const mouse = uiState.mouse;
    if (!mouse.mousedown || !mouse.position) return;

    // Calculate total delta from mousedown position
    const requestedDelta: Coords = {
      x: mouse.position.tile.x - mouse.mousedown.tile.x,
      y: mouse.position.tile.y - mouse.mousedown.tile.y
    };

    // Drag placement is tile-based. Browser mousemove events can fire many
    // times while the pointer remains inside one tile; replaying all model and
    // scene writes for those identical coordinates creates a render/update
    // feedback loop. Mouseup still performs the authoritative final commit.
    const dragTileKey = `${mouse.position.tile.x},${mouse.position.tile.y}`;
    if (lastProcessedDragTile === dragTileKey) return;
    lastProcessedDragTile = dragTileKey;

    const draggedKeys = new Set(items.map((item) => `${item.type}:${item.id}`));
    const moving = items.map((item): LayoutElement | null => {
      if (item.type === 'ITEM') {
        const tile = originalPositions.get(`item-${item.id}`);
        return tile ? { ref: item, from: { ...tile }, to: { ...tile } } : null;
      }
      if (item.type === 'RECTANGLE') {
        const bounds = originalPositions.get(`rect-${item.id}`);
        return bounds ? {
          ref: item,
          from: {
            x: Math.min(bounds.from.x, bounds.to.x),
            y: Math.min(bounds.from.y, bounds.to.y)
          },
          to: {
            x: Math.max(bounds.from.x, bounds.to.x),
            y: Math.max(bounds.from.y, bounds.to.y)
          }
        } : null;
      }
      if (item.type === 'TEXTBOX') {
        const tile = originalPositions.get(`textbox-${item.id}`);
        const textBox = scene.textBoxes.find((candidate: any) => candidate.id === item.id);
        return tile ? {
          ref: item,
          from: { ...tile },
          to: { x: tile.x + (textBox?.size?.width ?? 2), y: tile.y }
        } : null;
      }
      return null;
    }).filter((element): element is LayoutElement => Boolean(element));
    const stationary = [
      ...scene.items.map((item: any) => ({ type: 'ITEM' as const, id: item.id })),
      ...scene.rectangles.map((item: any) => ({ type: 'RECTANGLE' as const, id: item.id })),
      ...scene.textBoxes.map((item: any) => ({ type: 'TEXTBOX' as const, id: item.id }))
    ].filter((ref) => !draggedKeys.has(`${ref.type}:${ref.id}`))
      .map((ref) => getLayoutElement(ref, scene))
      .filter((element): element is LayoutElement => Boolean(element));
    // Tile coordinates are already discrete. A one-tile tolerance makes the
    // item jump ahead/behind the cursor and renders some tiles unreachable.
    // Use exact matches for object-relative guides without changing the tile
    // selected by the user's pointer.
    const snap = snapToObjects(moving, stationary, requestedDelta, 0);
    const totalDelta = snap.delta;
    uiState.actions.setSmartGuides(snap.guides);

    // Note: We intentionally DON'T return early when totalDelta is (0,0)
    // This allows the user to drag an item and place it back at its original position

    // Check if hovering over a group for drop target feedback
    const dropTargetGroupId = findDropTargetGroup(mouse.position.tile, scene, items);
    uiState.actions.setDropTargetGroupId(dropTargetGroupId);

    items.forEach((item) => {
      try {
        if (item.type === 'ITEM') {
          const originalTile = originalPositions.get(`item-${item.id}`);
          if (originalTile) {
            const targetTile = {
              x: originalTile.x + totalDelta.x,
              y: originalTile.y + totalDelta.y
            };
            scene.updateViewItem(item.id, { tile: targetTile });
          }

        } else if (item.type === 'RECTANGLE') {
          const originalBounds = originalPositions.get(`rect-${item.id}`);
          if (originalBounds) {
            scene.updateRectangle(item.id, {
              from: {
                x: originalBounds.from.x + totalDelta.x,
                y: originalBounds.from.y + totalDelta.y
              },
              to: {
                x: originalBounds.to.x + totalDelta.x,
                y: originalBounds.to.y + totalDelta.y
              }
            });
          }

        } else if (item.type === 'TEXTBOX') {
          const originalTile = originalPositions.get(`textbox-${item.id}`);
          if (originalTile) {
            scene.updateTextBox(item.id, {
              tile: {
                x: originalTile.x + totalDelta.x,
                y: originalTile.y + totalDelta.y
              }
            });
          }

        } else if (item.type === 'CONNECTOR_ANCHOR') {
          const connector = getAnchorParent(item.id, scene.connectors);
          const originalTile = originalPositions.get(`anchor-${item.id}`);

          if (originalTile) {
            const newConnector = produce(connector, (draft) => {
              const anchorIndex = draft.anchors.findIndex(a => a.id === item.id);

              if (anchorIndex !== -1) {
                const targetTile = {
                  x: originalTile.x + totalDelta.x,
                  y: originalTile.y + totalDelta.y
                };

                // Detach item endpoints while they are being moved. On drop,
                // the anchor is reattached if it lands on another item.
                draft.anchors[anchorIndex].ref = { tile: targetTile };
              }
            });

            // ✅ Just update position - NO simplification during drag
            // Let the user drag freely, only simplify on mouseup
            scene.updateConnector(connector.id, {
              anchors: newConnector.anchors
            });
          }
        }
      } catch (error) {
        console.error(`Error dragging ${item.type} ${item.id}:`, error);
      }
    });
  },

  mousedown: () => {
    // No action needed on mousedown during drag
  },

  mouseup: ({ uiState, scene, model }) => {
    // ✨ NEW: Final simplification cleanup after drag completes
    if (uiState.mode.type === 'DRAG_ITEMS') {
      const { items } = uiState.mode;

      // Finalize only the actively dragged waypoint at the release tile. This
      // preserves every meaningful bend and removes only a redundant point on
      // the straight span between its neighbours.
      items
        .filter((item) => item.type === 'CONNECTOR_ANCHOR')
        .forEach((item) => {
          try {
            const connector = getAnchorParent(item.id, scene.connectors);
            const dropTile = { ...uiState.mouse.position.tile };
            // The dragged connector anchor is the topmost hit at this tile.
            // Search all hits for a node so an occupied tile creates a real
            // item attachment instead of leaving a free tile endpoint.
            const itemAtDrop = getItemsAtTile({ tile: dropTile, scene })
              .find((candidate) => candidate.type === 'ITEM');
            const movedAnchors = connector.anchors.map((anchor) =>
              anchor.id === item.id
                ? {
                    ...anchor,
                    ref: itemAtDrop?.type === 'ITEM'
                      ? { item: itemAtDrop.id }
                      : { tile: dropTile }
                  }
                : anchor
            );
            const anchors = normalizeConnectorAnchors(movedAnchors, scene.currentView);
            scene.updateConnector(connector.id, { anchors });
            if (!anchors.some((anchor) => anchor.id === item.id)) {
              uiState.actions.setItemControls({ type: 'CONNECTOR', id: connector.id });
            }
          } catch (error) {
            console.warn(`Could not finalize connector waypoint ${item.id}:`, error);
          }
        });

      // ✨ NEW: Check if dragging a single ITEM onto another ITEM for replacement
      const draggedItems = items.filter(i => i.type === 'ITEM');
      if (draggedItems.length === 1) {
        const draggedItemId = draggedItems[0].id;
        const mousePos = uiState.mouse.position.tile;

        // Search every hit instead of only the topmost render layer. A
        // connector anchor or text label can share the target tile and would
        // otherwise hide the icon underneath from replacement detection.
        const itemAtDropPos = getItemsAtTile({
          tile: mousePos,
          scene,
          excludeIds: [draggedItemId]
        }).find((candidate) => candidate.type === 'ITEM');

        // An exact icon-on-icon collision is an explicit replacement gesture
        // and takes precedence over the broader nearby-group drop target.
        if (
          itemAtDropPos?.type === 'ITEM' &&
          itemAtDropPos.id !== draggedItemId
        ) {
          // Get the dragged item's icon from model
          const draggedModelItem = model.items.find((m) => m.id === draggedItemId);

          if (draggedModelItem?.icon) {
            // Store the replacement parameters
            iconReplacementPending = {
              sourceItemId: draggedItemId,
              targetItemId: itemAtDropPos.id,
              sourceIconId: draggedModelItem.icon
            };

            // Revert the dragged item to its original position
            const originalTile = originalPositions.get(`item-${draggedItemId}`);
            if (originalTile) {
              scene.updateViewItem(draggedItemId, { tile: originalTile });
            }

            // Trigger confirmation dialog
            if (showDragReplaceDialog) {
              showDragReplaceDialog({
                sourceItemId: draggedItemId,
                targetItemId: itemAtDropPos.id,
                sourceIconId: draggedModelItem.icon
              });
            }

            // Clear tracking data but don't exit mode yet - wait for dialog
            originalPositions.clear();
            uiState.actions.setDropTargetGroupId(null);
            uiState.actions.setSmartGuides([]);
            uiState.actions.setMode({
              type: 'CURSOR',
              showCursor: true,
              mousedownItem: null
            });
            return; // Wait for confirmation dialog
          }
        }
      }

      // Check if we're dropping on a group
      const dropTargetGroupId = uiState.dropTargetGroupId;
      if (dropTargetGroupId) {
        // Add each dragged item to the target group (if not already in it)
        items.forEach((item) => {
          if (item.type === 'CONNECTOR_ANCHOR') return;
          const element: GroupableElement = { type: item.type as any, id: item.id };
          const currentGroup = groupManager.getGroupForElement(element);

          // Only add if not already in this group
          if (!currentGroup || currentGroup.id !== dropTargetGroupId) {
            // Remove from current group if in one
            if (currentGroup) {
              groupManager.removeFromGroup(element);
            }

            // Add to target group
            const result = groupManager.addToGroup(dropTargetGroupId, element);
            if (result.success) {
              console.log(`✅ Added ${item.type} ${item.id} to group ${dropTargetGroupId}`);
            } else {
              console.warn(`⚠️ Failed to add ${item.type} ${item.id} to group:`, result.error);
            }
          }
        });
      }

      // Endpoint attachment and every meaningful user-defined bend are kept.
    }
    // ✅ Clear tracking data
    originalPositions.clear();

    // Clear drop target highlight
    uiState.actions.setDropTargetGroupId(null);
    uiState.actions.setSmartGuides([]);

    // Return to cursor mode
    uiState.actions.setMode({
      type: 'CURSOR',
      showCursor: true,
      mousedownItem: null
    });
  }
};
